/**
 * useLoginAuthAdapter — shared custom HOOK behind pos-web's 3 auth adapter hooks.
 *
 * NAMING (F8 / F6-RULEGAP-1, 2026-07-15): this was `createLoginAuthAdapter`
 * until the react-hooks rules were wired into the `@puffin-core` web-package
 * tier. It calls `useState` / `useEffect` / `useCallback` / `useMemo` and runs
 * during render, so by React's own definition it IS a custom hook and MUST be
 * named `use*`. The old `create*` name was not a cosmetic problem: React's
 * `rules-of-hooks` keys on the naming convention, so the name suppressed the
 * checker for this file AND for every call site — a conditional call of
 * `createLoginAuthAdapter()` could never have been flagged. Renaming restores
 * the checker across the whole chain.
 *
 * NOT to be confused with `createMarketingLoginAuth`, which is a GENUINE
 * factory: it is invoked once at module scope and RETURNS a hook
 * (`useMarketingLoginAuth`). That name is correct and stays.
 *
 * Before Item 8 of this enhancement pass, `useTenantPosAuth`,
 * `useMarketingLoginAuth`, and `useCustomerLoginAuth` each implemented the
 * same three concerns inline:
 *
 *   1. Wrap a base auth shape (`login` / `verifyMfa` / `cancelMfa` / etc.).
 *   2. Translate thrown errors via `translateAuthError` (Item 1).
 *   3. Fire canonical auth events via `onAuthEvent` (Item 5).
 *
 * The factory consolidates those three concerns. App-specific concerns that
 * are NOT shared — POS's PIN flow, station-unlock, and device-fingerprint —
 * stay local to the consuming hook and compose this factory for the shared
 * parts.
 *
 * The public API matches what `<LoginForm>` / `<LoginShell>` consume as `auth`:
 * `{ auth: AuthContract }`.
 *
 * NO PASSKEY ARM (USER-FACING-GEMS L1b, 2026-09-29). This hook used to take
 * `features: { passkey, passkeyService }` and return `passkeyProps` driven by a
 * service's `authenticate(context)`. It had no production consumer and could never
 * work: a passkey is bound to the tenant origin's rpId, and the only
 * `authenticate({ subdomain })` implementation (pos-web's local WebAuthnService)
 * was deleted in SCALE-ACTIVATE-001 S3-20. Passkey sign-in is ONE path —
 * `usePasskeyLogin` + `authenticateWithDiscoverablePasskey` — which builds the
 * shell's `passkeyProps` on the tenant login page.
 *
 * @module @puffin-core/login-shell/hooks/useLoginAuthAdapter
 */

import { useCallback, useMemo, useState } from 'react';
import { AuthError, translateAuthError } from '../utils/errorTranslator.js';
import { MFA_PASSKEY_METHOD, isPasskeyPromptDismissed } from '../utils/mfaPasskeyAnswer.js';

// Keep AuthError exported via this module so Item 8 tests + future consumers
// can reach it without a second import. Not used internally past the factory
// contract, but part of the factory's public-surface contract.
void AuthError;

/**
 * Fire the host's observability callback, when it supplied one. No catch (SWALLOWED-CATCH-BURNDOWN-001
 * U3): the callback is the HOST's code, and a throwing one is a defect in it — swallowed, it vanished
 * with no trace anywhere. The one shipped handler is `<MarketingCrossDomainLogin>`'s, which forwards
 * to marketing-shell's `AnalyticsProvider.track` — and that cannot throw (`safeEmit` drops a name
 * outside CONVERSION_EVENTS, which every `auth.*` name is).
 */
function fireAuthEvent(handler, type, payload) {
  if (typeof handler !== 'function') return;
  handler(type, payload);
}

/**
 * Build the `{auth}` shape consumed by `<LoginForm>`.
 *
 * @param {Object} params
 * @param {import('../types').AuthContract} params.base  The concrete auth
 *   implementation (from `usePosAuth`, a reducer, a customer AuthContext, etc).
 * @param {(result: any) => void} [params.onSuccess]
 *   Called on successful login or MFA verify (when `onMfaSuccess` is absent).
 * @param {(result: any) => void} [params.onMfaSuccess]
 *   Called on successful MFA verify; preferred over `onSuccess` when provided.
 * @param {import('../types').AuthEventHandler} [params.onAuthEvent]
 *   Observability callback. A throwing handler is the host's defect and surfaces.
 * @param {(err: unknown, operation: ('login'|'mfa')) => (Error & {code?: ?string, errorClass?: ?string})}
 *   [params.errorTranslator] Custom translator for thrown errors. Defaults to `translateAuthError`. It answers an
 *   `AuthError` for a failure it words, or any other `Error` it leaves unworded (re-auth's `ReAuthDenial`, whose
 *   host shows its own sentence): the adapter reads `code` / `errorClass` only when present.
 * @returns {{auth: import('../types').AuthContract}}
 */
export function useLoginAuthAdapter({
  base,
  onSuccess,
  onMfaSuccess,
  onAuthEvent,
  errorTranslator = translateAuthError,
} = {}) {
  if (!base || typeof base.login !== 'function') {
    throw new Error(
      'useLoginAuthAdapter requires `base` with at least a `login(email, password, captchaToken, extras?)` function.',
    );
  }

  // LOGIN-CONVERGENCE W9 (2026-10-11): the adapter owns the in-flight flag. A surface whose sign-in is
  // a bare `login(...)` function (the owner console's session, the storefront shopper's) has no
  // `isLoading` of its own, and the form reads `auth.isLoading` to disable its fields and say
  // "Signing in…". A base that reports one still does: the two are OR-ed.
  const [inFlight, setInFlight] = useState(false);

  const login = useCallback(
    async (email, password, captchaToken, extras) => {
      fireAuthEvent(onAuthEvent, 'login.attempt', { email });
      let result;
      setInFlight(true);
      try {
        result = await base.login(email, password, captchaToken, extras);
      } catch (err) {
        const translated = errorTranslator(err, 'login');
        fireAuthEvent(onAuthEvent, 'login.failure', {
          email,
          code: translated.code ?? null,
          errorClass: translated.errorClass ?? null,
        });
        throw translated;
      } finally {
        setInFlight(false);
      }
      // N152: a held sign-in handed to the company's own sign-in page — neither a success nor a failure.
      if (result?.requiresMfaEnrollmentHandoff) {
        fireAuthEvent(onAuthEvent, 'mfa.enrollment_required', { email });
        return result;
      }
      if (result?.requiresMfa) {
        fireAuthEvent(onAuthEvent, 'mfa.required', {
          email,
          // Every adapter answers its methods (login convergence, 2026-10-19).
          methods: result.mfaMethods,
          preferredMethod: result.preferredMethod || null,
        });
        return result;
      }
      fireAuthEvent(onAuthEvent, 'login.success', { email });
      if (onSuccess) onSuccess(result);
      return result;
    },
    [base, onAuthEvent, onSuccess, errorTranslator],
  );

  // ONE tail for every answer to the challenge — a code, or a passkey (Q-EE-34 P5): translate a refusal, report it, and
  // hand a success to the surface. A dismissed passkey prompt is no server's refusal, so it passes untranslated and the
  // MFA screen words it (`mfaRefusalOf`).
  const answerMfaChallenge = useCallback(
    async (verify, method) => {
      let result;
      try {
        result = await verify();
      } catch (err) {
        const translated = isPasskeyPromptDismissed(err) ? err : errorTranslator(err, 'mfa');
        fireAuthEvent(onAuthEvent, 'mfa.failure', {
          method: method || null,
          code: translated.code ?? null,
          errorClass: translated.errorClass ?? null,
        });
        throw translated;
      }
      fireAuthEvent(onAuthEvent, 'mfa.success', { method: method || null });
      if (onMfaSuccess) onMfaSuccess(result);
      else if (onSuccess) onSuccess(result);
      return result;
    },
    [onAuthEvent, onMfaSuccess, onSuccess, errorTranslator],
  );

  // Surfaced on `auth.verifyMfa` only when `hasMfa` (below): `base.verifyMfa` is a function.
  const verifyMfa = useCallback(
    (code, method) => answerMfaChallenge(() => base.verifyMfa(code, method), method),
    [base, answerMfaChallenge],
  );

  // Surfaced on `auth.verifyMfaWithPasskey` only when the base serves a passkey (below).
  const verifyMfaWithPasskey = useCallback(
    (getAssertion) =>
      answerMfaChallenge(() => base.verifyMfaWithPasskey(getAssertion), MFA_PASSKEY_METHOD),
    [base, answerMfaChallenge],
  );

  // Error-surface contract: `base.error` flows through unchanged. Adapters
  // are responsible for ensuring the string is safe to render (either null,
  // or a pre-translated generic from `translateAuthError`). This preserves
  // per-operation specificity (an MFA flow can surface `GENERIC_MFA_ERROR`
  // without the factory overwriting it with `GENERIC_LOGIN_ERROR`).
  //
  // See `useCustomerLoginAuth` (sanitizes customerAuth.error at the bridge),
  // `useMarketingLoginAuth` (reducer dispatches pre-translated messages),
  // and `useTenantPosAuth` (sanitizes posAuth.error at its memoized output).

  const hasMfa = typeof base.verifyMfa === 'function';
  const hasPasskeyMfa = typeof base.verifyMfaWithPasskey === 'function';
  const auth = useMemo(
    () => ({
      login,
      // Preserve the pre-factory contract: customer-mode consumers that don't
      // provide `base.verifyMfa` get `auth.verifyMfa === undefined` (not a
      // no-op function), so <LoginForm> + introspecting tests behave identically.
      verifyMfa: hasMfa ? verifyMfa : undefined,
      // Q-EE-34 P5: likewise `undefined` where the base serves no passkey — the MFA screen then offers none.
      verifyMfaWithPasskey: hasPasskeyMfa ? verifyMfaWithPasskey : undefined,
      cancelMfa: base.cancelMfa,
      clearError: base.clearError,
      sendMfaOtp: base.sendMfaOtp,
      isLoading: Boolean(base.isLoading) || inFlight,
      error: base.error ?? null,
      mfaRequired: Boolean(base.mfaRequired),
      mfaMethods: base.mfaMethods || [],
      preferredMethod: base.preferredMethod || null,
      maskedEmail: base.maskedEmail || null,
      maskedPhone: base.maskedPhone || null,
      mfaEnrollmentHandoff: base.mfaEnrollmentHandoff || null,
    }),
    [
      login,
      verifyMfa,
      hasMfa,
      verifyMfaWithPasskey,
      hasPasskeyMfa,
      base.cancelMfa,
      base.clearError,
      base.sendMfaOtp,
      base.isLoading,
      inFlight,
      base.error,
      base.mfaRequired,
      base.mfaMethods,
      base.preferredMethod,
      base.maskedEmail,
      base.maskedPhone,
      base.mfaEnrollmentHandoff,
    ],
  );

  return { auth };
}

export default useLoginAuthAdapter;
